'use client';

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from '@/context/LanguageContext';
import { localeNames, localeFlags } from '@/i18n';
import type { Locale } from '@/i18n';
import styles from './LanguagePicker.module.scss';

interface LanguagePickerProps {
  compactOnMobile?: boolean;
}

export default function LanguagePicker({ compactOnMobile = false }: LanguagePickerProps) {
  const { locale, activeLocales, setLocale } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(l: Locale) {
    setLocale(l);
    setOpen(false);
  }

  return (
    <div className={styles.picker} ref={ref}>
      <button
        className={`${styles.trigger} ${compactOnMobile ? styles.compactOnMobile : ''}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Language: ${localeNames[locale]}`}
      >
        <span className={styles.flag}>{localeFlags[locale]}</span>
        <span className={styles.code}>{localeNames[locale]}</span>
        <svg className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <ul className={styles.dropdown} role="listbox" aria-label="Select language">
          {activeLocales.map((l) => (
            <li key={l} role="option" aria-selected={l === locale}>
              <button
                className={`${styles.option} ${l === locale ? styles.optionActive : ''}`}
                onClick={() => handleSelect(l)}
              >
                <span className={styles.flag}>{localeFlags[l]}</span>
                <span className={styles.optionLabel}>{localeNames[l]}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
